/**
 * イメージボードの内部クリップボード（同一ブラウザ内のコピー & ペースト用）。
 *
 * システムクリップボードは画像を PNG 1 枚としてしか運べないため、
 * - ボード上でリサイズした `width` / `height`（拡大縮小）が落ちる
 * - 貼り付けのたびに同じ画像を S3 へ再アップロードすることになる
 * という 2 つの問題がある。
 *
 * そこでコピー時に「S3 キー + ボード上のサイズ + 選択範囲内の相対位置」を
 * sessionStorage に控えておき、同じボードへの貼り付けでは
 * アップロードを挟まずコピー元と同じ大きさで複製する。
 * システムクリップボードへの PNG 書き出しは外部アプリ向けに従来どおり併用する。
 *
 * 別ボードや別ブラウザへの貼り付けは、この控えが使えないので
 * 従来どおり PNG ファイルとしてアップロードされる。
 */

const STORAGE_KEY = "soremo.imageboard.clipboard";

/**
 * 控えの有効期限。これを過ぎた S3 キーは元アイテムが削除されて実体が
 * 消えている可能性が高いので、素直にファイル経由の貼り付けへ倒す。
 */
const TTL_MS = 60 * 60 * 1000;

export interface BoardClipboardEntry {
  /** コピー元アイテムの S3 キー。貼り付け時はこれを再利用する。 */
  image_key: string;
  /** コピー元のボード上サイズ（拡大縮小をそのまま再現する）。 */
  width: number;
  height: number;
  /** 選択範囲の左上を原点とした相対位置（複数選択の配置を保つ）。 */
  offsetX: number;
  offsetY: number;
}

export interface BoardClipboardPayload {
  /** コピー元のボード（ノート ID）。複製できるのはそのボードにある S3 キーだけ。 */
  boardId: string;
  copiedAt: number;
  entries: BoardClipboardEntry[];
}

const isEntry = (value: unknown): value is BoardClipboardEntry => {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.image_key === "string" &&
    e.image_key.length > 0 &&
    typeof e.width === "number" &&
    typeof e.height === "number" &&
    typeof e.offsetX === "number" &&
    typeof e.offsetY === "number"
  );
};

/** 選択中のアイテムを内部クリップボードへ控える（コピー操作から同期的に呼ぶ）。 */
export const writeBoardClipboard = (
  boardId: string,
  entries: BoardClipboardEntry[],
): void => {
  if (!boardId || entries.length === 0) return;
  const payload: BoardClipboardPayload = {
    boardId,
    copiedAt: Date.now(),
    entries,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage が使えない環境では従来のファイル経由の貼り付けに任せる
  }
};

/** 内部クリップボードを読む。未設定・壊れている・期限切れなら null。 */
export const readBoardClipboard = (): BoardClipboardPayload | null => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const payload = parsed as Record<string, unknown>;
  if (typeof payload.boardId !== "string") return null;
  if (typeof payload.copiedAt !== "number") return null;
  if (Date.now() - payload.copiedAt > TTL_MS) return null;
  if (!Array.isArray(payload.entries)) return null;

  const entries = payload.entries.filter(isEntry);
  if (entries.length === 0) return null;

  return {
    boardId: payload.boardId,
    copiedAt: payload.copiedAt,
    entries,
  };
};
